import { type JSX, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useLocation } from 'react-router';

import { useConsultReplies } from '@/features/watch-consult-replies';
import { toLocaleDigits } from '@/shared/lib/format';
import { Button, CloseIcon, MessageIcon } from '@/shared/ui';

const DISMISSED_KEY = 'reply-notice:dismissed';

function readDismissed(): string {
  try {
    return sessionStorage.getItem(DISMISSED_KEY) ?? '';
  } catch {
    // private mode or blocked storage: the notice simply comes back next time
    return '';
  }
}

function writeDismissed(signature: string): void {
  try {
    sessionStorage.setItem(DISMISSED_KEY, signature);
  } catch {
    // see readDismissed
  }
}

/**
 * «پاسخ تازه از پزشک» — a notice at the bottom of every patient screen, just
 * above the tab bar, when the doctor has answered on «مشاوره» and the patient
 * has not opened it.
 *
 * The clinic's report: an answer arrived and nobody on the patient's side
 * could tell without opening «مشاوره». One sentence, one button to the answer,
 * one to put it away. Put away, it stays away for *these* answers this
 * session, and comes back for the next one. Not shown on «مشاوره» itself,
 * where every unread conversation already carries its own «پاسخ تازه».
 *
 * A live region, polite: it is announced, never interrupts (§7.5). Text and an
 * icon, never colour alone.
 */
export function ReplyNotice(): JSX.Element | null {
  const { t, i18n } = useTranslation('records');
  const { pathname } = useLocation();
  const replies = useConsultReplies();
  const [dismissed, setDismissed] = useState(readDismissed);

  const hidden =
    replies.count === 0 || replies.signature === dismissed || pathname.startsWith('/consult');

  return (
    <div
      aria-live="polite"
      className="pointer-events-none fixed inset-x-0 z-40 px-4 bottom-[calc(var(--tab-bar-height)+var(--safe-bottom)+var(--space-3))] sm:bottom-[calc(var(--tab-bar-height)+var(--safe-bottom)+var(--space-6))]"
      role="status"
    >
      {hidden ? null : (
        <div className="page-enter pointer-events-auto mx-auto flex max-w-md items-center gap-3 rounded-[var(--radius-lg)] border border-border bg-surface p-3 shadow-md">
          <span
            aria-hidden="true"
            className="flex size-10 shrink-0 items-center justify-center rounded-full bg-primary-tint text-primary-ink"
          >
            <MessageIcon className="size-5" />
          </span>

          <p className="min-w-0 flex-1 type-caption text-text">
            <span className="block font-semibold">{t('consult.replyNotice.title')}</span>
            {t('consult.replyNotice.body', {
              count: replies.count,
              n: toLocaleDigits(String(replies.count), i18n.language),
            })}
          </p>

          <Button asChild size="sm" variant="primary">
            <Link to="/consult">{t('consult.replyNotice.open')}</Link>
          </Button>

          <button
            aria-label={t('consult.replyNotice.dismiss')}
            className="tap-target -me-1 inline-flex shrink-0 items-center justify-center rounded-full text-muted hover:bg-raised hover:text-text"
            onClick={(): void => {
              writeDismissed(replies.signature);
              setDismissed(replies.signature);
            }}
            type="button"
          >
            <CloseIcon className="size-4" />
          </button>
        </div>
      )}
    </div>
  );
}
